import { paths } from "../../routes/paths";

/**
 * Sidebar navigation items.
 *
 * IMPORTANT: labels are stored as i18n KEYS (labelKey + labelDefault),
 * NOT pre-translated strings. Translation happens at RENDER time inside
 * the Sidebar component via useTranslation()'s t(), so the menu follows
 * live language switches. (The old version called i18n.t() at module
 * load: the labels were frozen in whatever language was active at app
 * startup and never updated when the user changed language.)
 *
 * Destinations come from `routes/paths.ts`, not string literals. The menu is
 * the app's most-clicked set of links, so it is the worst place to carry a
 * path that no route matches -- and a literal typo there is invisible until a
 * user lands on the 404. A missing or misspelled builder is a compile error.
 */
export interface SidebarItem {
  labelKey: string;
  labelDefault: string;
  iconName: string;
  width: number;
  height: number;
  to?: string;
  /**
   * Subscription feature this entry needs.
   *
   * Optional: when omitted, the Sidebar falls back to `ROUTE_FEATURE_MAP` in
   * SubscriptionContext, which is the same table the click handler consults.
   * Set it here only when an entry needs a gate its route does not.
   */
  requiredFeature?: string;
  comingSoon?: boolean;
}

export const items: SidebarItem[] = [
  {
    labelKey: "sidebar.dashboard",
    labelDefault: "Dashboard",
    iconName: "dashboard",
    width: 24,
    height: 24,
    to: paths.dashboard,
  },
  {
    labelKey: "sidebar.aiAgent",
    labelDefault: "AI Assistant",
    iconName: "aiAgent",
    width: 24,
    height: 24,
    to: paths.aiAgent,
  },
  {
    labelKey: "sidebar.colaborators",
    labelDefault: "Colaborators",
    iconName: "collaborateur",
    width: 24,
    height: 24,
    to: paths.collaborator.list,
  },
  {
    labelKey: "sidebar.car",
    labelDefault: "Car",
    iconName: "vehicule",
    width: 24,
    height: 24,
    to: paths.vehicle.list,
  },
  {
    labelKey: "sidebar.notifications",
    labelDefault: "Notifications",
    iconName: "notification",
    width: 24,
    height: 24,
    to: paths.notifications.list,
  },
  {
    labelKey: "sidebar.claims",
    labelDefault: "Claims",
    iconName: "sinistre",
    width: 24,
    height: 24,
    to: paths.claims.list,
  },
  {
    labelKey: "sidebar.CardsAndBadges",
    labelDefault: "Cards & badges",
    iconName: "certificate",
    width: 24,
    height: 24,
    to: paths.cardsAndBadges,
  },
  {
    labelKey: "sidebar.documents",
    labelDefault: "Documents",
    iconName: "fileStack",
    width: 24,
    height: 24,
    to: paths.documents.list,
  },
  {
    labelKey: "sidebar.geolocation",
    labelDefault: "Geolocation",
    iconName: "map2",
    width: 24,
    height: 24,
    to: paths.geolocation,
  },
  // Analyse & optimisation. The feature gate makes the sidebar open the
  // upgrade modal instead of navigating to a page the API would refuse anyway;
  // it is resolved from ROUTE_FEATURE_MAP, so it cannot drift from the check
  // the click handler performs.
  {
    labelKey: "sidebar.fiscalite",
    labelDefault: "Tax optimisation",
    iconName: "tax",
    width: 24,
    height: 24,
    to: paths.fiscalite,
  },
  {
    labelKey: "sidebar.analyses",
    labelDefault: "Advanced analytics",
    iconName: "analytics",
    width: 24,
    height: 24,
    to: paths.analyses,
  },
  {
    /*
     * TCO was routed and had SEO metadata but appeared in no navigation
     * anywhere -- no sidebar entry, no link, no navigate() call -- so the page
     * was reachable only by typing the URL. It carries no `requiredFeature`
     * because the route is gated by RequireSubscription alone.
     */
    labelKey: "sidebar.tco",
    labelDefault: "Total cost of ownership",
    iconName: "currency",
    width: 24,
    height: 24,
    to: paths.tco,
  },
  {
    labelKey: "sidebar.services",
    labelDefault: "Services",
    iconName: "stars",
    width: 24,
    height: 24,
    comingSoon: true,
  },
  {
    labelKey: "sidebar.billing",
    labelDefault: "Billing",
    iconName: "license",
    width: 24,
    height: 24,
    to: paths.billing,
  },
  {
    labelKey: "sidebar.helper",
    labelDefault: "Helper",
    iconName: "help",
    width: 24,
    height: 24,
    to: paths.aide,
  },
  {
    labelKey: "sidebar.setting",
    labelDefault: "Setting",
    iconName: "settings",
    width: 24,
    height: 24,
    to: paths.parametre,
  },
  {
    labelKey: "sidebar.logout",
    labelDefault: "Logout",
    iconName: "logout",
    width: 24,
    height: 24,
  },
];
